import {
  LAUNCH,
  SOCIALS,
  FOLLOW_CTA,
  secondsUntilHatch,
} from '@trumpybird/brand';

const root = document.getElementById('root')!;

type Stage =
  | 'pristine'
  | 'hairline'
  | 'branching'
  | 'dramatic'
  | 'imminent'
  | 'hatched';

interface StageInfo {
  stage: Stage;
  /** 0..1 progression *within* the current stage. */
  progress01: number;
  /** 0..1 monotonically-increasing overall pre-hatch urgency. */
  urgency: number;
}

function stageFor(secs: number): StageInfo {
  if (secs <= 0) return { stage: 'hatched', progress01: 1, urgency: 1 };
  const H = 3600;
  let stage: Stage;
  let progress01 = 0;
  if (secs > 48 * H) {
    stage = 'pristine';
    const cap = 90 * 24 * H;
    const clamped = Math.min(secs - 48 * H, cap);
    progress01 = 1 - clamped / cap;
  } else if (secs > 24 * H) {
    stage = 'hairline';
    progress01 = 1 - (secs - 24 * H) / (24 * H);
  } else if (secs > 6 * H) {
    stage = 'branching';
    progress01 = 1 - (secs - 6 * H) / (18 * H);
  } else if (secs > 15 * 60) {
    stage = 'dramatic';
    progress01 = 1 - (secs - 15 * 60) / (6 * H - 15 * 60);
  } else {
    stage = 'imminent';
    progress01 = 1 - secs / (15 * 60);
  }
  const totalWindow = 72 * H;
  const urgency = Math.min(1, Math.max(0, 1 - secs / totalWindow));
  return { stage, progress01, urgency };
}

/** Interpolated crack-layer opacities [early, late]. Two clean layers only. */
function crackOpacities(info: StageInfo): [number, number] {
  const { stage, progress01 } = info;
  // Early cracks: appear across hairline → branching, hold thereafter.
  const early =
    stage === 'pristine'
      ? 0
      : stage === 'hairline'
        ? progress01
        : 1;
  // Late cracks: appear across dramatic → imminent, hold at hatched.
  const late =
    stage === 'dramatic'
      ? progress01
      : stage === 'imminent' || stage === 'hatched'
        ? 1
        : 0;
  return [early, late];
}

/**
 * Tiny deterministic pseudo-random for illustration scatter.
 * Seeded so the scene is byte-identical on every render.
 */
function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

/**
 * Minimalist background: one clean vertical gradient (patriot-blue → cream)
 * plus three low-opacity patriot-white stars in the upper third and a single
 * high, slow-drifting cream cloud. That's it. Less is more.
 */
function backgroundSceneSvg(): string {
  // Three seeded five-pointed star positions in the upper third.
  const stars = (() => {
    const r = seeded(2026);
    const points: string[] = [];
    const spots: Array<[number, number, number]> = [
      [220, 130, 14],
      [640, 90, 11],
      [980, 180, 13],
    ];
    for (const [cx, cy, size] of spots) {
      // Nudge slightly with seeded jitter so the trio doesn't feel gridded.
      const x = cx + (r() - 0.5) * 20;
      const y = cy + (r() - 0.5) * 20;
      const path = starPath(x, y, size, size * 0.42);
      points.push(
        `<path d="${path}" fill="#F8F4E3" opacity="0.30"/>`,
      );
    }
    return points.join('');
  })();

  return `
    <svg class="scene" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stop-color="#3C3B6E"/>
          <stop offset="60%" stop-color="#3C3B6E"/>
          <stop offset="100%" stop-color="#F8F4E3"/>
        </linearGradient>
        <symbol id="cloud" viewBox="0 0 240 80">
          <path d="M 20 55
                   Q 30 30 60 32
                   Q 74 14 100 22
                   Q 124 12 148 28
                   Q 176 22 190 42
                   Q 220 46 218 62
                   Q 200 72 160 68
                   Q 110 74 70 70
                   Q 30 72 20 55 Z"
                fill="#F8F4E3" fill-opacity="0.55"/>
        </symbol>
      </defs>

      <rect width="1200" height="800" fill="url(#sky)"/>

      <!-- Three subtle patriot-white stars, upper third, low opacity. -->
      <g class="stars">${stars}</g>

      <!-- One high, slow cream cloud (120s drift). -->
      <g class="cloud-drift" opacity="0.9">
        <use href="#cloud" x="-120" y="160" width="360" height="120"/>
      </g>
    </svg>`;
}

/** Five-pointed star SVG path centered on (cx,cy) with outer/inner radii. */
function starPath(cx: number, cy: number, ro: number, ri: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? ro : ri;
    const a = (i * Math.PI) / 5 - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return `M ${pts[0]} L ${pts.slice(1).join(' L ')} Z`;
}

/**
 * Woven straw palette — sanctioned in DESIGN.md §1.3 scene shades.
 */
const NEST_STRAW = ['#c9974a', '#e8c176', '#8a5a1e', '#a97a30', '#b5843a'];

function strawStrand(
  x: number,
  y: number,
  len: number,
  rot: number,
  color: string,
  w: number,
): string {
  return `<path d="M ${x.toFixed(1)} ${y.toFixed(1)} q ${(len / 2).toFixed(1)} ${(-6 + (rot % 5)).toFixed(1)} ${len.toFixed(1)} ${((rot % 7) - 3).toFixed(1)}"
    stroke="${color}" stroke-width="${w}" stroke-linecap="round" fill="none"
    transform="rotate(${rot.toFixed(1)} ${(x + len / 2).toFixed(1)} ${y.toFixed(1)})"/>`;
}

/** Back-facing strands (rendered behind the egg). */
function nestBackSvg(): string {
  const r = seeded(7717);
  const strands: string[] = [];
  for (let i = 0; i < 24; i++) {
    const x = 30 + i * 15 + Math.floor(r() * 8);
    const y = 48 + Math.floor(r() * 22);
    const rot = -25 + Math.floor(r() * 50);
    const c = NEST_STRAW[Math.floor(r() * NEST_STRAW.length)] ?? NEST_STRAW[0]!;
    const w = 2 + Math.floor(r() * 3);
    const len = 34 + Math.floor(r() * 18);
    strands.push(strawStrand(x, y, len, rot, c, w));
  }
  return `
    <svg class="nest nest-back" viewBox="0 0 420 180" aria-hidden="true">
      <path d="M 20 90 Q 40 40 210 30 Q 380 40 400 90
               Q 390 155 210 165 Q 30 155 20 90 Z"
            fill="#a97a30"
            stroke="#1a1830" stroke-opacity="0.85" stroke-width="4"
            stroke-linejoin="round"/>
      <ellipse cx="210" cy="92" rx="150" ry="30" fill="#3a2612" opacity="0.35"/>
      <g class="nest-strands-back">${strands.join('')}</g>
    </svg>`;
}

/** Fore-facing strands (rendered in front of the lower egg edge). */
function nestFrontSvg(): string {
  const r = seeded(9091);
  const strands: string[] = [];
  for (let i = 0; i < 26; i++) {
    const x = 25 + i * 14 + Math.floor(r() * 10);
    const y = 118 + Math.floor(r() * 30);
    const rot = -30 + Math.floor(r() * 60);
    const c = NEST_STRAW[Math.floor(r() * NEST_STRAW.length)] ?? NEST_STRAW[0]!;
    const w = 3 + Math.floor(r() * 3);
    const len = 36 + Math.floor(r() * 22);
    strands.push(strawStrand(x, y, len, rot, c, w));
  }
  const sitShadow = `<ellipse cx="210" cy="140" rx="90" ry="10" fill="#1a1830" opacity="0.35"/>`;
  const groundShadow = `<ellipse cx="210" cy="172" rx="200" ry="10" fill="#1a1830" opacity="0.30"/>`;
  return `
    <svg class="nest nest-front" viewBox="0 0 420 180" aria-hidden="true">
      ${groundShadow}
      ${sitShadow}
      <g class="nest-strands-front">${strands.join('')}</g>
      <path d="M 20 90 Q 30 140 210 165 Q 390 140 400 90"
            stroke="#1a1830" stroke-opacity="0.85" stroke-width="4"
            fill="none" stroke-linejoin="round" stroke-linecap="round"/>
    </svg>`;
}

/**
 * Restrained speckle scatter (~18) — texture, not measles.
 */
const EGG_SPECKLES: string = (() => {
  const r = seeded(1337);
  const specks: string[] = [];
  const cx = 130;
  const cy = 170;
  const rx = 96;
  const ry = 138;
  const palette = ['#FFD700', '#B22234', '#d9b954', '#c9974a'];
  const count = 18;
  let placed = 0;
  let guard = 0;
  while (placed < count && guard < count * 12) {
    guard++;
    const x = 20 + r() * 220;
    const y = 20 + r() * 290;
    const nx = (x - cx) / (rx - 10);
    const ny = (y - cy) / (ry - 14);
    if (nx * nx + ny * ny > 1) continue;
    const rad = 2.2 + r() * 3.2;
    const color = palette[Math.floor(r() * palette.length)];
    const alpha = (0.45 + r() * 0.4).toFixed(2);
    specks.push(
      `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${rad.toFixed(1)}" fill="${color}" opacity="${alpha}"/>`,
    );
    placed++;
  }
  return specks.join('');
})();

function eggSvg(info: StageInfo): string {
  const [l1, l2] = crackOpacities(info);
  const outline = '#1a1830';
  return `
    <svg class="egg-svg" viewBox="0 0 260 320" aria-hidden="true">
      <defs>
        <clipPath id="eggClip">
          <path d="M 130 12 C 210 12 236 130 236 200
                   C 236 278 188 306 130 306
                   C 72 306 24 278 24 200
                   C 24 130 50 12 130 12 Z"/>
        </clipPath>
        <radialGradient id="innerGlow" cx="50%" cy="30%" r="60%">
          <stop offset="0%" stop-color="#fff2a8" stop-opacity="1"/>
          <stop offset="55%" stop-color="#ffd700" stop-opacity="0.6"/>
          <stop offset="100%" stop-color="#ffd700" stop-opacity="0"/>
        </radialGradient>
      </defs>

      <g clip-path="url(#eggClip)">
        <rect x="0" y="0" width="260" height="320" fill="url(#innerGlow)"
              opacity="${(0.15 + info.urgency * 0.85).toFixed(2)}"/>
        <rect x="0" y="0" width="260" height="320" fill="#fdf8e7"/>
        <path d="M 24 200 C 24 278 72 306 130 306
                 C 188 306 236 278 236 200
                 C 210 232 180 244 130 244
                 C 80 244 50 232 24 200 Z"
              fill="#c9b8d6" opacity="0.55"/>
        <path d="M 130 12 C 175 12 205 46 220 96
                 C 200 108 170 116 130 116
                 C 108 116 88 112 70 104
                 C 82 52 100 12 130 12 Z"
              fill="#fff5b3" opacity="0.85"/>
        <g class="egg-speckles">${EGG_SPECKLES}</g>
      </g>

      <path d="M 130 12 C 210 12 236 130 236 200
               C 236 278 188 306 130 306
               C 72 306 24 278 24 200
               C 24 130 50 12 130 12 Z"
            fill="none" stroke="${outline}" stroke-width="3" stroke-linejoin="round"/>
      <path d="M 40 250 C 60 292 96 306 130 306 C 164 306 200 292 220 250"
            fill="none" stroke="${outline}" stroke-width="5" stroke-linecap="round" opacity="0.8"/>

      <ellipse cx="130" cy="312" rx="80" ry="6" fill="${outline}" opacity="0.30"/>

      <!-- Two clean crack layers, that's all. -->
      <g stroke="${outline}" stroke-linecap="round" fill="none" opacity="${l1.toFixed(2)}">
        <path d="M 118 44 l 10 22 l -8 16 l 14 20" stroke-width="3"/>
      </g>
      <g stroke="${outline}" stroke-linecap="round" fill="none" opacity="${l2.toFixed(2)}">
        <path d="M 90 130 l 22 20 l -14 22 l 26 20 l -10 22" stroke-width="3.2"/>
      </g>
    </svg>`;
}

function renderCountdown(secs: number, urgency: number): string {
  if (secs <= 0) {
    return `<div class="hatched">Trumpy has hatched! Bigly!</div>`;
  }
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const hot = urgency > 0.6 ? 'unit-hot' : '';
  const unit = (n: number, l: string, key: string): string =>
    `<div class="unit ${hot}" data-key="${key}"><span class="num" data-val="${n}">${String(n).padStart(2, '0')}</span><span class="label">${l}</span></div>`;
  return `
    <div class="countdown" aria-live="polite">
      ${unit(d, 'Days', 'd')}
      ${unit(h, 'Hours', 'h')}
      ${unit(m, 'Minutes', 'm')}
      ${unit(s, 'Seconds', 's')}
    </div>`;
}

/* Inline platform icons (16px on the landing), monochrome via fill inherit. */
const ICONS: Record<string, string> = {
  tiktok: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.5 3c.4 2 1.8 3.7 3.9 4.1v2.4c-1.5.1-2.8-.3-4-1v6.9c0 4-3.2 6.6-6.6 6.6-3.4 0-6.1-2.7-6.1-6.1 0-3.4 2.7-6.1 6.1-6.1.3 0 .6 0 .9.1v2.5c-.3-.1-.6-.1-.9-.1-1.9 0-3.5 1.6-3.5 3.6s1.6 3.6 3.5 3.6c1.9 0 3.6-1.5 3.6-3.6V3h3.1z"/></svg>`,
  youtube: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M23 7.2s-.2-1.6-.9-2.3c-.9-.9-1.8-.9-2.3-1C16.5 3.6 12 3.6 12 3.6s-4.5 0-7.8.3c-.5.1-1.4.1-2.3 1C1.2 5.6 1 7.2 1 7.2S.8 9 .8 10.9v1.7C.8 14.4 1 16.2 1 16.2s.2 1.6.9 2.3c.9.9 2.1.9 2.6 1 1.9.2 8 .3 8.5.3s4.5 0 7.8-.3c.5-.1 1.4-.1 2.3-1 .7-.7.9-2.3.9-2.3s.2-1.8.2-3.6v-1.7c0-1.9-.2-3.7-.2-3.7zM9.7 14.6V8.4l5.9 3.1-5.9 3.1z"/></svg>`,
  x: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,
  instagram: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.9.9 1.4.2.4.4 1 .4 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.9.7-1.4.9-.4.2-1 .4-2.2.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.9-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.9-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2M12 0C8.7 0 8.3 0 7.1.1 5.8.1 4.9.3 4.1.6 3.3.9 2.6 1.3 1.9 2c-.7.7-1.1 1.4-1.4 2.2C.3 5 .1 5.9.1 7.1 0 8.3 0 8.7 0 12s0 3.7.1 4.9c.1 1.2.3 2.1.6 2.9.3.8.7 1.5 1.4 2.2.7.7 1.4 1.1 2.2 1.4.8.3 1.7.5 2.9.6 1.2.1 1.6.1 4.9.1s3.7 0 4.9-.1c1.2-.1 2.1-.3 2.9-.6.8-.3 1.5-.7 2.2-1.4.7-.7 1.1-1.4 1.4-2.2.3-.8.5-1.7.6-2.9.1-1.2.1-1.6.1-4.9s0-3.7-.1-4.9c-.1-1.2-.3-2.1-.6-2.9-.3-.8-.7-1.5-1.4-2.2-.7-.7-1.4-1.1-2.2-1.4-.8-.3-1.7-.5-2.9-.6C15.7 0 15.3 0 12 0zm0 5.8c-3.4 0-6.2 2.8-6.2 6.2s2.8 6.2 6.2 6.2 6.2-2.8 6.2-6.2S15.4 5.8 12 5.8zm0 10.2c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4zm7.8-10.4c0 .8-.7 1.4-1.4 1.4-.8 0-1.4-.7-1.4-1.4 0-.8.7-1.4 1.4-1.4.8 0 1.4.6 1.4 1.4z"/></svg>`,
  twitch: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.149 0L.523 4.286V21.43h5.98V24h3.34l2.571-2.571h4.858L23.478 15.6V0H2.149zm19.157 14.571l-3.428 3.429h-5.484l-2.57 2.571v-2.571H4.834V1.714h16.472v12.857zM19.5 5.014v6.857h-2.057V5.014H19.5zm-5.485 0v6.857h-2.057V5.014h2.057z"/></svg>`,
  kick: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3h5v5.4h1.8V6.6h1.8V4.8h1.8V3H19v5.4h-1.8v1.8H15.4V12h1.8v1.8H19V19h-5.6v-1.8h-1.8v-1.8H9.8V19H3V3z"/></svg>`,
  telegram: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.643.135-.953l11.566-4.458c.538-.196 1.006.128.832.941z"/></svg>`,
};

function followRowHtml(): string {
  const order = ['tiktok', 'youtube', 'x', 'instagram', 'twitch', 'kick', 'telegram'];
  const items = order
    .map((k) => {
      const s = SOCIALS[k];
      if (!s) return '';
      const icon = ICONS[k] ?? '';
      return `<a href="${s.url}" target="_blank" rel="noopener" aria-label="${s.platform} ${s.handle}">${icon}<span>${s.handle}</span></a>`;
    })
    .join('');
  return `
    <div class="follow-row" aria-label="Follow TrumpyBird">
      <div class="follow-label">${FOLLOW_CTA}</div>
      ${items}
    </div>`;
}

function scaffold(): void {
  root.innerHTML = `
    <div class="bg" aria-hidden="true">${backgroundSceneSvg()}</div>
    <!-- TODO: media-content — when the Flux-rendered hero raster lands at
         apps/site/public/hero/hero-<variant>.png (see brand/MEDIA.md prompt
         library), set --hero-image on :root to url('/hero/hero-*.png'). The
         SVG scene above renders correctly with --hero-image: none. -->
    <div class="hero-art" aria-hidden="true"></div>
    <main class="hero">
      <span class="stars-badge">${LAUNCH.americaBirthday}</span>
      <div class="stage" aria-hidden="true">
        <div class="glow"></div>
        <!-- Depth order: back-nest → egg → front-nest so the egg sits INSIDE the nest. -->
        <div class="nest-wrap nest-wrap-back">${nestBackSvg()}</div>
        <div class="egg-wrap">
          <div class="egg" id="egg"></div>
        </div>
        <div class="nest-wrap nest-wrap-front">${nestFrontSvg()}</div>
      </div>
      <h1>Something tremendous is hatching.</h1>
      <p class="tagline">
        Meet <em>Trumpy</em> — the greatest cartoon eagle in the history of eagles, <em>believe me</em>.
        Hatching live on <em>${LAUNCH.hatchDisplayShort}</em>.
      </p>
      <p class="whyline">
        Laid in a golden mountaintop nest during a fireworks show. He picked
        America's biggest birthday to make his entrance — <em>tremendous timing</em>.
      </p>
      <div id="countdownMount"></div>
      <form class="email" id="signup" autocomplete="off" novalidate>
        <input
          type="email"
          name="email"
          placeholder="your@email.com"
          required
          aria-label="Email"
        />
        <button type="submit">Notify Me</button>
      </form>
      <p class="form-msg" id="formMsg" role="status"></p>
      <a class="btn-secondary" href="${SOCIALS.twitch!.url}" target="_blank" rel="noopener">
        Watch the nest live on Twitch
      </a>
      ${followRowHtml()}
    </main>
    <footer class="footer">
      <span class="l">&copy; 2026 TrumpyBird</span>
      <span class="r">A cartoon eagle for America 250 &middot; No affiliation with any real person</span>
    </footer>
  `;

  const form = document.getElementById('signup') as HTMLFormElement | null;
  const msg = document.getElementById('formMsg');
  const input = form?.elements.namedItem('email') as HTMLInputElement | null;
  if (form && msg && input) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = input.value.trim();
      msg.className = 'form-msg';
      input.classList.remove('error');
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        input.classList.add('error');
        msg.textContent = 'That email looks a little off — try again.';
        msg.className = 'form-msg error';
        return;
      }
      msg.textContent = 'Nesting your address...';
      try {
        const endpoint = (import.meta.env.VITE_SIGNUP_ENDPOINT as string | undefined) ?? '';
        if (endpoint) {
          await fetch(endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ email, source: 'prelaunch' }),
          });
        }
        msg.textContent = "You're on the nest list! We'll ping you when Trumpy hatches.";
        msg.className = 'form-msg success';
        form.reset();
      } catch {
        msg.textContent = 'Something went wrong — try again in a moment.';
        msg.className = 'form-msg error';
      }
    });
  }
}

let lastSecondVal = -1;

function tick(): void {
  const secs = secondsUntilHatch();
  const info = stageFor(secs);

  const egg = document.getElementById('egg');
  if (egg) {
    egg.innerHTML = eggSvg(info);
    egg.dataset.stage = info.stage;
    const stageEl = egg.closest('.stage') as HTMLElement | null;
    if (stageEl) {
      stageEl.style.setProperty('--urgency', info.urgency.toFixed(3));
      stageEl.dataset.stage = info.stage;
    }
  }
  document.documentElement.style.setProperty('--urgency', info.urgency.toFixed(3));

  const mount = document.getElementById('countdownMount');
  if (mount) mount.innerHTML = renderCountdown(secs, info.urgency);

  const secEl = document.querySelector(
    '.countdown .unit[data-key="s"] .num',
  ) as HTMLElement | null;
  if (secEl) {
    const v = Number(secEl.dataset.val);
    if (v !== lastSecondVal) {
      secEl.classList.remove('digit-bounce');
      void secEl.offsetWidth;
      secEl.classList.add('digit-bounce');
      lastSecondVal = v;
    }
  }
}

scaffold();
tick();
setInterval(tick, 1000);
